import TemplateDropdown from "src/components/template/template-dropdown";
import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTeamSprints } from "src/scenes/teams/dashboard/selected-sprints-provider-teams";
import { useSprints } from "src/scenes/dashboard/selected-sprint-provider";
import { useEffect, useState } from "react";
import request from "src/utils/request";
import { useAuth } from "src/components/providers/auth-context";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
} from "@mui/material";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

const JiraProjectSelector = ({ queryVariablesSetter, value, setValue }) => {
    const { teamId } = useParams();
    const [searchParams] = useSearchParams();
    const [projects, setProjects] = useState<{ projectKey: string; default?: boolean }[]>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const useSprint = teamId ? useTeamSprints() : useSprints();
    const { isDemo, userHasTeamsSubscription } = useAuth();

    const selectedTeam = searchParams.get("selectedTeam");
    const effectiveTeamId =
        teamId || (selectedTeam && UUID_REGEX.test(selectedTeam) ? selectedTeam : null);

    const getSprints = async (id) => {
        if (id)
            if (userHasTeamsSubscription) {
                if (UUID_REGEX.test(id)) {
                    const res = await request<{
                        projects: { projectKey: string; default?: boolean }[];
                    }>(`/api/data/fundamentals/teams/${id}/v1/jira-projects`, {
                        method: "GET",
                    });
                    setProjects(res.projects);
                } else {
                    const res = await request<{
                        projects: { projectKey: string; default?: boolean }[];
                    }>(`/api/data/fundamentals/v1/jira-projects`, {
                        method: "GET",
                    });
                    setProjects(res.projects);
                }
            }
        if (isDemo || !userHasTeamsSubscription) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            const res = await import(
                "../../scenes/dashboard/mock-data/work-fundamentals/jira-projects"
            );
            setProjects(res.default);
        }
    };

    useEffect(() => {
        if (teamId) setProjects(useSprint?.allProjects?.projects || undefined);
        if (selectedTeam) getSprints(selectedTeam);
    }, [teamId, selectedTeam, useSprint]);

    const handleSetDefault = async () => {
        if (!effectiveTeamId || !value) return;
        await request(`/api/data/fundamentals/teams/${effectiveTeamId}/v1/jira-projects/default`, {
            method: "PUT",
            body: JSON.stringify({ projectKey: value }),
        });
        setProjects((prev) => prev?.map((p) => ({ ...p, default: p.projectKey === value })));
        setDialogOpen(false);
    };

    if (!queryVariablesSetter || !queryVariablesSetter.project) return null;
    if (!projects || !projects.length) return null;

    const isDefaultProject = !!projects.find((p) => p.projectKey === value)?.default;
    const showSetDefaultButton = !!effectiveTeamId && value !== "All" && !isDefaultProject;

    return (
        <>
            <Stack direction="row" alignItems="center" gap={1}>
                <TemplateDropdown
                    options={null}
                    title="Jira Project"
                    allValue="All"
                    dynamicOptions={queryVariablesSetter.project.dynamicOptions}
                    value={value}
                    setValue={setValue}
                    data={{ projects }}
                />
                {showSetDefaultButton && (
                    <Button size="small" variant="outlined" onClick={() => setDialogOpen(true)}>
                        Set as default
                    </Button>
                )}
            </Stack>
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>Set default Jira project</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will set <strong>{value}</strong> as the default Jira project for the
                        entire team. All team members will see it pre-selected when they open the
                        dashboard.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSetDefault}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default JiraProjectSelector;
